import React, { FC, useState, ChangeEvent, useRef } from 'react'
import styles from './FileInput.module.scss'
import { Text } from 'components/Text'
import { useId } from '@reach/auto-id'
import { Icon } from 'components/Icon'
import { useTranslation } from 'react-i18next'
import { Flex } from 'components/Flex'

type FileValue = {
  name: string
  url: string
}

type FileInputProps = {
  label: string
  name?: string
  value?: FileValue
  description?: string
  accept?: string
  required?: boolean
  onChange?: (data: File) => void
}

export const FileInput: FC<FileInputProps> = ({
  description,
  label,
  name,
  value,
  onChange,
  accept,
  required = false,
}) => {
  const { t } = useTranslation()
  const inputId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<any>(value)
  const desc = description || t('file_input.description')

  const onInputChanged = (event: ChangeEvent<HTMLInputElement>) => {
    const data = event.target.files?.[0]

    if (!data) return

    const file = {
      name: data.name,
      url: URL.createObjectURL(data),
    }

    setFile(file)
    onChange && onChange(data)
  }

  const resetImage = () => {
    setFile(undefined)
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  return (
    <Flex justify="between" className={styles.fileInput}>
      {isImage(file) && (
        <div className={styles.preview}>
          <img src={file!.url} alt={file!.name} />
          <button onClick={resetImage} aria-label={t('file_input.remove_image')}>
            <Icon name="close-fill" />
          </button>
        </div>
      )}
      <div className={styles.description}>
        <label htmlFor={inputId}>
          <Text weight="strong">{label}</Text>
        </label>
        <Text size="small" truncate>
          {file ? file.name : desc}
        </Text>
      </div>
      <label htmlFor={inputId} className={styles.label}>
        {t('file_input.cta')}
      </label>
      <input
        id={inputId}
        type="file"
        onChange={onInputChanged}
        ref={inputRef}
        accept={accept}
        required={required}
      />
    </Flex>
  )
}

FileInput.displayName = 'FileInput'

const isImage = (file?: FileValue) => {
  // eslint-disable-next-line eqeqeq
  if (file == undefined) return false

  const extension = file.name.substring(file.name.lastIndexOf('.') + 1)
  return ['png', 'jpg', 'jpeg', 'gif'].includes(extension)
}
